import { useEffect, useState } from "react";
import { X, Info, AlertTriangle, CheckCircle2, AlertOctagon } from "lucide-react";
import { fetchSupportBanner } from "@/lib/api";

const TONE = {
    info: {
        bg: "bg-bl-teal-tint",
        text: "text-bl-primary",
        Icon: Info,
    },
    success: {
        bg: "bg-emerald-100",
        text: "text-emerald-800",
        Icon: CheckCircle2,
    },
    warning: {
        bg: "bg-amber-100",
        text: "text-amber-900",
        Icon: AlertTriangle,
    },
    critical: {
        bg: "bg-[#C85B3A]/15",
        text: "text-[#C85B3A]",
        Icon: AlertOctagon,
    },
};

const SESSION_KEY = "bl_support_banner_dismissed_v1";

/**
 * SupportBanner — sitewide calm banner controlled by admins. Polls once on
 * mount (DB → no AI). Dismissible per banner_id (session-scoped) when the
 * admin allows it.
 */
export default function SupportBanner() {
    const [banner, setBanner] = useState(null);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const b = await fetchSupportBanner();
                if (!mounted) return;
                setBanner(b);
                if (b?.banner_id) {
                    try {
                        const v = sessionStorage.getItem(SESSION_KEY);
                        if (v === b.banner_id) setDismissed(true);
                    } catch {
                        /* ignore */
                    }
                }
            } catch {
                /* fail silently — banner is optional */
            }
        })();
        return () => {
            mounted = false;
        };
    }, []);

    if (!banner || !banner.active || dismissed) return null;
    const tone = TONE[banner.tone] || TONE.info;
    const { Icon } = tone;

    const onDismiss = () => {
        try {
            sessionStorage.setItem(SESSION_KEY, banner.banner_id);
        } catch {
            /* ignore */
        }
        setDismissed(true);
    };

    return (
        <div
            className={`w-full ${tone.bg} ${tone.text}`}
            data-testid="support-banner"
            role="status"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3 text-sm">
                <Icon className="w-4 h-4 shrink-0" />
                <p className="flex-1 leading-snug">{banner.message}</p>
                {banner.link_url && (
                    <a
                        href={banner.link_url}
                        target={banner.link_url.startsWith("http") ? "_blank" : undefined}
                        rel="noreferrer"
                        className="underline font-medium whitespace-nowrap"
                        data-testid="support-banner-link"
                    >
                        {banner.link_label || "Learn more"}
                    </a>
                )}
                {banner.dismissible && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        className="opacity-70 hover:opacity-100 p-1"
                        aria-label="Dismiss banner"
                        data-testid="support-banner-dismiss"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
}
